import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionInspections } from './hooks/useInspections';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { InspectionStatus, InspectionType, InspectionResult } from './types/construction-inspections.types';

const statusColors: Record<InspectionStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  requested: 'bg-amber-100 text-amber-600',
  scheduled: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-indigo-100 text-indigo-600',
  submitted: 'bg-violet-100 text-violet-600',
  passed: 'bg-emerald-100 text-emerald-600',
  failed: 'bg-red-100 text-red-600',
  rework_required: 'bg-orange-100 text-orange-600',
  reinspection_required: 'bg-rose-100 text-rose-600',
  approved: 'bg-emerald-600 text-white',
  closed: 'bg-slate-900 text-white',
  cancelled: 'bg-slate-200 text-slate-400',
};

const statusLabels: Record<InspectionStatus, string> = {
  draft: 'Bản thảo',
  requested: 'Đã yêu cầu',
  scheduled: 'Đã lập lịch',
  in_progress: 'Đang kiểm tra',
  submitted: 'Đã nộp kết quả',
  passed: 'Đạt (Passed)',
  failed: 'Không đạt (Failed)',
  rework_required: 'Yêu cầu sửa lỗi',
  reinspection_required: 'Cần kiểm tra lại',
  approved: 'Đã phê duyệt',
  closed: 'Đã đóng',
  cancelled: 'Đã hủy',
};

const typeLabels: Record<InspectionType, string> = {
  incoming_material: 'Kiểm tra vật tư đầu vào',
  pre_installation: 'Kiểm tra trước thi công',
  in_process: 'Kiểm tra trong quá trình',
  hold_point: 'Điểm dừng kiểm tra (Hold Point)',
  final_acceptance: 'Nghiệm thu hoàn thành',
  handover: 'Bàn giao',
  safety: 'Kiểm tra an toàn',
  mockup: 'Kiểm tra mẫu (Mockup)',
  defect_check: 'Kiểm tra sai sót (Defect)',
  rework_verification: 'Xác minh sửa lỗi',
};

const resultColors: Record<InspectionResult, string> = {
  pass: 'text-emerald-500',
  fail: 'text-red-500',
  conditional_pass: 'text-amber-500',
  not_applicable: 'text-slate-400',
  pending: 'text-slate-300',
};

export default function InspectionListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    inspections,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useConstructionInspections(companyId);

  return (
    <PageLayout
      title="Phiếu Kiểm tra Thi công (Inspections)"
      description="Trung tâm quản lý các yêu cầu kiểm tra kỹ thuật, xác minh thực địa và nghiệm thu các điểm dừng (Hold points)."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Kiểm tra' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-calendar-event-line">Lịch kiểm tra</Button>
          <AuthButton
            module="quality"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Yêu cầu kiểm tra
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tỷ lệ đạt (Pass)"
            value={Math.round((stats?.passedCount || 0) / (stats?.totalInspections || 1) * 100)}
            icon="ri-checkbox-circle-line"
            variant="success"
            suffix="%"
          />
          <KPICard
            title="Đang chờ xử lý"
            value={stats?.pendingCount || 0}
            icon="ri-time-line"
            variant="warning"
          />
          <KPICard
            title="Số phiếu kiểm tra"
            value={stats?.totalInspections || 0}
            icon="ri-file-list-3-line"
            variant="primary"
          />
          <KPICard
            title="Điểm số TB"
            value={Math.round(stats?.avgScore || 0)}
            icon="ri-numbers-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã phiếu, tiêu đề kiểm tra..."
                value={filters.search}
                onSearch={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'type', label: 'Loại kiểm tra', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(typeLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã phiếu' },
              { key: 'title', title: 'Nội dung kiểm tra' },
              { key: 'inspector', title: 'Người kiểm tra / Ngày' },
              { key: 'result', title: 'Kết quả', align: 'center' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={inspections}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary  tracking-wider">{item.inspection_code}</span>;
                case 'title':
                  return (
                    <div className="flex flex-col max-w-xs">
                      <span className="font-bold text-slate-900 line-clamp-1">{item.title}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold ">{typeLabels[item.inspection_type]}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-medium italic">{item.zone?.name || 'Toàn dự án'}</span>
                      </div>
                    </div>
                  );
                case 'inspector':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.inspector?.full_name} size="xs" src={item.inspector?.avatar_url} />
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700 underline">{item.inspector?.full_name || 'Chưa phân công'}</span>
                        <span className="text-[9px] text-slate-400 italic">{item.inspected_at ? new Date(item.inspected_at).toLocaleDateString('vi-VN') : '---'}</span>
                      </div>
                    </div>
                  );
                case 'result':
                  return (
                    <div className={`flex items-center justify-center ${resultColors[item.result_code]}`}>
                      <i className={`ri-${item.result_code === 'pass' ? 'checkbox-circle' : item.result_code === 'fail' ? 'close-circle' : 'time'}-fill text-lg`} />
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
